import * as React from 'react';
import styled from 'styled-components';

import {
  NotificationIconV1,
  NotificationSecondaryButtonV1,
  NotificationTextButtonV1,
} from '@oneaudi/audi-notification-display-service';
import {
  CMP_COLOR_FEEDBACK_NOTIFICATION_FOREGROUND_ICON_DEFAULT_DARK,
  SYS_COLOR_FOREGROUND_SIGNAL_NEGATIVE_DARK,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
} from '@oneaudi/unified-web-common';
import { Layout, LayoutItem, Text, Icon, IconButton } from '@oneaudi/unified-web-components';
import { SYS_COLOR_FOREGROUND_SIGNAL_POSITIVE_100_DARK } from '@oneaudi/unified-web-alpha-common';

export enum PopupNotificationType {
  ERROR = 'error',
  GENERIC = 'generic',
  SUCCESS = 'success',
}

const ICON_SIZE = 24;
const TITLE_MARGIN_TOP = 2;

interface PopupNotificationProps {
  type: PopupNotificationType;
  notificationId?: string;
  closeLabel?: string;
  title?: string;
  icon?: NotificationIconV1;
  textButton?: NotificationTextButtonV1;
  secondaryButton?: NotificationSecondaryButtonV1;
  onClose?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
}

interface StyledNotificationProps {
  'data-notificationid'?: string;
  role?: string;
  children?: React.ReactNode;
}

interface StyledIconType {
  type: PopupNotificationType;
}

const StyledNotification = styled.div<StyledNotificationProps>`
  overflow: hidden;
`;

const StyledNotificationContentContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const getColor = (type: PopupNotificationType) => {
  switch (type) {
    case PopupNotificationType.ERROR:
      return SYS_COLOR_FOREGROUND_SIGNAL_NEGATIVE_DARK;
    case PopupNotificationType.SUCCESS:
      return SYS_COLOR_FOREGROUND_SIGNAL_POSITIVE_100_DARK;
    default:
      return CMP_COLOR_FEEDBACK_NOTIFICATION_FOREGROUND_ICON_DEFAULT_DARK;
  }
};

const StyledIcon = styled.div<StyledIconType>`
  display: grid;
  place-items: center;

  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  color: ${({ type }) => getColor(type)};
`;

const StyledNotificationContent = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  padding-left: ${SYS_SPACE_FIXED_150};
`;

const StyledTitle = styled.div`
  margin-top: ${TITLE_MARGIN_TOP}px;
`;

export const PopupNotification: React.FunctionComponent<PopupNotificationProps> = (
  props,
): React.ReactElement => {
  const {
    type,
    notificationId,
    children,
    title,
    icon,
    textButton,
    secondaryButton,
    onClose,
    closeLabel,
  } = props;
  const { ERROR, GENERIC, SUCCESS } = PopupNotificationType;

  return (
    <StyledNotification data-notificationid={notificationId} role="alert">
      <StyledNotificationContentContainer>
        <Layout padding={[SYS_SPACE_FIXED_200]}>
          {type !== GENERIC && (
            <StyledIcon type={type}>
              {type === SUCCESS && <Icon name="select-circle" size="s" />}
              {type === ERROR && <Icon name="caution" size="s" />}
            </StyledIcon>
          )}
          {type === GENERIC && icon && (
            <LayoutItem>
              <StyledIcon type={type}>{icon}</StyledIcon>
            </LayoutItem>
          )}
          <StyledNotificationContent>
            <Layout direction="column">
              {typeof title === 'string' && (
                <StyledTitle>
                  <Text tag="p" variant={{ order: '2', style: 'bold' }}>
                    {title}
                  </Text>
                </StyledTitle>
              )}
              <LayoutItem>{children}</LayoutItem>
              {secondaryButton && <LayoutItem>{secondaryButton}</LayoutItem>}
              {textButton && (
                <LayoutItem margin={['var(--spacing-relative-md)', 0, 0, 0]}>
                  {textButton}
                </LayoutItem>
              )}
            </Layout>
          </StyledNotificationContent>
        </Layout>
        {onClose && (
          <IconButton
            variant="tertiary"
            icon="cancel"
            size="sm"
            aria-label={closeLabel || 'close'}
            onClick={onClose}
          />
        )}
      </StyledNotificationContentContainer>
    </StyledNotification>
  );
};
